import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ShieldAlert, Mail } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/dashboard-layout'
import { useAuth } from '@/hooks/useAuth'

import { AnalyticsPage } from '@/pages/analytics'
import { CommercePage } from '@/pages/commerce'
import { ContentPage } from '@/pages/content'
import { LeaderboardPage } from '@/pages/leaderboard'
import { OverviewPage } from '@/pages/overview'
import { SettingsPage } from '@/pages/settings'
import { SupportPage } from '@/pages/support'
import { UsersPage } from '@/pages/users'
import { UserDetailPage } from '@/pages/user-detail'
import { TranslationsPage } from '@/pages/translations'
import { BannersPage } from '@/pages/banners'
import { AdminManagementPage } from '@/pages/admin-management'
import { AppSettingsPage } from '@/pages/app-settings'
import { PushNotifications } from '@/pages/push-notifications'
import { LoginPage } from '@/pages/login'
import { PrivacyPolicyPublicPage } from '@/pages/privacy-policy-public'
import { TermsOfServicePublicPage } from '@/pages/terms-of-service-public'
import { StravaSupportPublicPage } from '@/pages/strava-support-public'
import { QrDownloadPublicPage } from '@/pages/qr-download-public'
import { ProfilePage } from '@/pages/profile'

const BYPASS_AUTH_FOR_LOCAL_REVIEW = false

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
})

// Access Denied Page for non-admin users
function AccessDeniedPage() {
  const { user, signOutUser, error } = useAuth()
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] px-6">
      <div className="max-w-md w-full">
        <div className="relative">
          {/* Glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-rose-600 to-orange-600 rounded-3xl blur opacity-20" />
          
          <div className="relative bg-[#141414] rounded-3xl border border-stone-800 p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mx-auto mb-6">
              <ShieldAlert className="h-10 w-10 text-rose-400" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
            <p className="text-stone-400 mb-6">
              {error || "You don't have permission to access this dashboard."}
            </p>
            
            {user?.email && (
              <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-stone-900/50 border border-stone-800 mb-6">
                <Mail className="h-4 w-4 text-stone-500" />
                <span className="text-stone-300 text-sm">{user.email}</span>
              </div>
            )}
            
            <div className="space-y-3">
              <p className="text-stone-500 text-sm">
                If you believe this is a mistake, please contact your administrator.
              </p>
              
              <button
                onClick={() => void signOutUser()}
                className="w-full py-3 px-4 rounded-xl bg-stone-800 text-stone-300 font-medium hover:bg-stone-700 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, loading } = useAuth()

  if (BYPASS_AUTH_FOR_LOCAL_REVIEW) {
    return <>{children}</>
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-stone-800" />
            <div className="absolute inset-0 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
          </div>
          <p className="text-stone-400 font-medium">Authenticating...</p>
          <p className="text-stone-600 text-sm mt-1">Please wait</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <LoginPage />
  }

  if (!isAdmin) {
    return <AccessDeniedPage />
  }

  return <>{children}</>
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public Standalone Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPublicPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePublicPage />} />
          <Route path="/strava-support" element={<StravaSupportPublicPage />} />
          <Route path="/qr-download" element={<QrDownloadPublicPage />} />

          {/* Protected Dashboard Layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<OverviewPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:uid" element={<UserDetailPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="content" element={<ContentPage />} />
            <Route path="commerce" element={<CommercePage />} />
            <Route path="translations" element={<TranslationsPage />} />
            <Route path="leaderboard" element={<LeaderboardPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="banners" element={<BannersPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="app-settings" element={<AppSettingsPage />} />
            <Route path="push-notifications" element={<PushNotifications />} />
            <Route path="admin-management" element={<AdminManagementPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
